import { create } from 'zustand';
import { temporal } from 'zundo';
import {
  GameObject,
  Transform,
  Component,
  GameObjectType,
  DEFAULT_TRANSFORM
} from '../../core/types/gameobject.types';
import { SceneEditorState } from '../../core/types/project-state.types';
import { BUILT_IN_COMPONENTS } from '../constants/components';
import { DEFAULT_CASCADE_SLOT_CONFIG } from '../../core/types/cascade-slot.types';
import { useSceneViewStore } from './sceneViewStore';

// Simple ID generator (nanoid alternative for now)
const generateId = () => Math.random().toString(36).substring(2, 11);

/**
 * Input type for prefab objects when instantiating from .prefab files
 */
export interface PrefabObjectInput {
  id: string;
  name: string;
  type?: string;
  parentId: string | null;
  children: string[];
  transform: {
    position: { x: number; y: number };
    rotation: number;
    scale: { x: number; y: number };
  };
  components: Array<{ type?: string; data?: Record<string, unknown> } & Record<string, unknown>>;
}

// ============================================================================
// Scene Caching & File Format Types
// ============================================================================

/**
 * Cached scene state stored in memory for fast switching
 */
interface CachedSceneState {
  sceneId: string;
  sceneName: string;
  gameObjects: Record<string, GameObject>;
  rootIds: string[];
  selectedIds: string[];
  viewport: { panX: number; panY: number; zoom: number };
  cachedAt: number;
}

/**
 * Scene file format for .scene files on disk
 */
interface SceneFileFormat {
  version: number;
  name: string;
  objects: Array<{
    id: string;
    name: string;
    type: GameObjectType;
    tag: string;
    layer: string;
    active: boolean;
    visible: boolean;
    locked: boolean;
    expanded: boolean;
    parentId: string | null;
    childIds: string[];
    transform: Transform;
    components: Component[];
  }>;
  settings: {
    backgroundColor?: string;
  };
}

// ============================================================================
// Default Components Mapping
// ============================================================================
// Maps each game object type to its default component types
const DEFAULT_COMPONENTS_MAP: Record<GameObjectType, string[]> = {
  'empty': [],
  'container': [],
  'sprite': ['sprite-renderer'],
  'text': ['text-renderer'],
  'graphics': ['graphics-renderer'],
  'particle': ['particle-system'],
  'camera': ['camera'],
  'light': ['light'],
  'spine': ['spine-renderer', 'spine-animator'],
  'cascade-slot': ['cascade-slot-config'],
  'backend-slot-game': ['backend-game-config'],
};

/**
 * Creates default component instances from component type strings
 * Reads property defaults from BUILT_IN_COMPONENTS definitions
 */
function createDefaultComponents(componentTypes: string[]): Component[] {
  return componentTypes.map(componentType => {
    const definition = BUILT_IN_COMPONENTS.find(c => c.type === componentType);
    const defaultData: Record<string, unknown> = {};

    if (definition) {
      for (const prop of definition.properties) {
        if (prop.defaultValue !== undefined) {
          defaultData[prop.name] = prop.defaultValue;
        }
      }
    }

    // Cascade slot config gets the full DEFAULT_CASCADE_SLOT_CONFIG as data
    if (componentType === 'cascade-slot-config') {
      Object.assign(defaultData, DEFAULT_CASCADE_SLOT_CONFIG);
    }

    // Backend game config gets empty defaults
    if (componentType === 'backend-game-config') {
      Object.assign(defaultData, {
        gameName: '',
        gameId: '',
        name: '',
        version: '',
        description: '',
        backendConfig: null,
        activeMode: 0,
        symbolTextures: {},
        startingBalance: 10000,
        customBetLevels: null,
      });
    }

    return {
      id: generateId(),
      type: componentType,
      enabled: true,
      data: defaultData,
    };
  });
}

interface SceneState {
  // Scene data
  sceneId: string;
  sceneName: string;
  gameObjects: Record<string, GameObject>;
  rootIds: string[]; // Top-level object IDs

  // Scene switching state
  currentScenePath: string | null;
  sceneDirtyFlags: Record<string, boolean>;
  sceneCache: Record<string, CachedSceneState>;

  // Selection
  selectedIds: string[];
  hoveredId: string | null;
  focusedContainerId: string | null;

  // Clipboard
  clipboardIds: string[];
  clipboardOperation: 'cut' | 'copy' | null;

  // Actions
  createGameObject: (type: GameObjectType, parentId?: string | null) => string;
  deleteGameObject: (id: string) => void;
  deleteSelected: () => void;
  updateGameObject: (id: string, updates: Partial<GameObject>) => void;
  updateTransform: (id: string, transform: Partial<Transform>) => void;
  updateMultipleTransforms: (ids: string[], deltaX: number, deltaY: number) => void;
  addComponent: (id: string, component: Component) => void;
  removeComponent: (objectId: string, componentId: string) => void;
  updateComponent: (objectId: string, componentId: string, data: Record<string, unknown>) => void;

  // Selection
  select: (id: string, addToSelection?: boolean) => void;
  selectMultiple: (ids: string[]) => void;
  selectAll: () => void;
  clearSelection: () => void;
  setHovered: (id: string | null) => void;

  // Container focus (drill-down selection)
  setFocusedContainer: (id: string | null) => void;
  enterContainer: (id: string) => void;
  exitContainer: () => void;
  expandToObject: (id: string) => void;

  // Hierarchy operations
  reparent: (id: string, newParentId: string | null, insertIndex?: number) => void;
  duplicateGameObject: (id: string) => string;
  copySelected: () => void;
  cutSelected: () => void;
  paste: (parentId?: string | null) => void;

  // Visibility & Lock
  toggleVisibility: (id: string) => void;
  toggleLock: (id: string) => void;
  toggleExpanded: (id: string) => void;
  expandAll: () => void;
  collapseAll: () => void;

  // Prefab instantiation
  instantiatePrefab: (
    prefabData: { gameObjects: Record<string, PrefabObjectInput>; rootId: string },
    parentId: string | null,
    insertIndex?: number
  ) => string | null;

  // Utility
  getGameObject: (id: string) => GameObject | undefined;
  getChildren: (id: string) => GameObject[];
  getDescendants: (id: string) => GameObject[];
  findByName: (name: string) => GameObject[];
  isEffectivelyLocked: (id: string) => boolean;
  isEffectivelyVisible: (id: string) => boolean;

  // Scene switching methods
  cacheCurrentScene: (cacheKey?: string) => void;
  loadScene: (path: string) => Promise<void>;
  saveSceneToDisk: (path?: string) => Promise<void>;
  isSceneDirty: (path?: string) => boolean;
  markSceneDirty: () => void;
  markSceneClean: (path?: string) => void;

  // Project state methods
  loadProjectState: (state: SceneEditorState) => void;
  getProjectState: () => SceneEditorState;
  resetToDefaults: () => void;
}

const createDefaultGameObject = (type: GameObjectType, name: string): GameObject => ({
  id: generateId(),
  name,
  type,
  tag: 'Untagged',
  layer: 'Default',
  active: true,
  visible: true,
  locked: false,
  expanded: true,
  parentId: null,
  childIds: [],
  transform: { ...DEFAULT_TRANSFORM },
  components: [],
});

// ============================================================================
// Sub-Children Helpers for Slot Hierarchy
// ============================================================================

interface SubChildDef {
  name: string;
  type: GameObjectType;
  componentData?: Record<string, unknown>;
}

/**
 * Creates sprite/text sub-children under a parent part container.
 * Uses standard sprite-renderer and text-renderer components so the
 * existing inspector property editors work out of the box.
 */
function createSubChildren(
  parentId: string,
  subDefs: SubChildDef[],
  newGameObjects: Record<string, GameObject>,
): string[] {
  const childIds: string[] = [];
  for (const def of subDefs) {
    const child = createDefaultGameObject(def.type, def.name);
    child.parentId = parentId;
    child.transform = { ...child.transform, position: { x: 0, y: 0 } };
    const defaultComponentTypes = DEFAULT_COMPONENTS_MAP[def.type] || [];
    child.components = createDefaultComponents(defaultComponentTypes);
    if (def.componentData && child.components.length > 0) {
      child.components[0] = {
        ...child.components[0],
        data: { ...child.components[0].data, ...def.componentData },
      };
    }
    newGameObjects[child.id] = child;
    childIds.push(child.id);
  }
  return childIds;
}

/** Returns sub-child definitions for a given part type */
function getSubChildDefs(partType: string, context?: { symbolId?: string; gameName?: string }): SubChildDef[] {
  switch (partType) {
    case 'game_background':
    case 'slot_background':
      return [
        { name: 'Background', type: 'sprite' },
      ];
    case 'header':
      return [
        { name: 'Background', type: 'sprite' },
        { name: 'Title', type: 'text', componentData: { text: context?.gameName || 'Game Title', fontSize: 14, fill: '#ffffff', align: 'center' } },
      ];
    case 'symbol':
      return [
        { name: 'Sprite', type: 'sprite' },
        { name: 'Label', type: 'text', componentData: { text: context?.symbolId || '?', fontSize: 10, fill: '#ffffff', align: 'center' } },
      ];
    case 'win_banner':
      return [
        { name: 'Background', type: 'sprite' },
        { name: 'Win Label', type: 'text', componentData: { text: 'WIN', fontSize: 16, fill: '#fbbf24', fontWeight: 'bold', align: 'center' } },
        { name: 'Win Amount', type: 'text', componentData: { text: '0', fontSize: 20, fill: '#ffffff', fontWeight: 'bold', align: 'center' } },
      ];
    case 'balance_text':
      return [
        { name: 'Background', type: 'sprite' },
        { name: 'Icon', type: 'sprite' },
        { name: 'Text', type: 'text', componentData: { text: 'Balance', fontSize: 12, fill: '#ffffff', align: 'center' } },
      ];
    case 'buy_feature':
      return [
        { name: 'Background', type: 'sprite' },
        { name: 'Label', type: 'text', componentData: { text: 'BUY FEATURE', fontSize: 11, fill: '#ffffff', fontWeight: 'bold', align: 'center' } },
      ];
    case 'win_text':
      return [
        { name: 'Background', type: 'sprite' },
        { name: 'Icon', type: 'sprite' },
        { name: 'Text', type: 'text', componentData: { text: '0', fontSize: 14, fill: '#fbbf24', align: 'center' } },
      ];
    case 'spin_options':
      return [
        { name: 'Background', type: 'sprite' },
        { name: 'Icon', type: 'sprite' },
        { name: 'Label', type: 'text', componentData: { text: 'Spin Options', fontSize: 10, fill: '#ffffff', align: 'center' } },
      ];
    case 'spin_button':
      return [
        { name: 'Background', type: 'sprite' },
        { name: 'Icon', type: 'sprite' },
        { name: 'Label', type: 'text', componentData: { text: 'SPIN', fontSize: 14, fill: '#ffffff', fontWeight: 'bold', align: 'center' } },
      ];
    case 'bet_selector':
      return [
        { name: 'Background', type: 'sprite' },
        { name: 'Icon', type: 'sprite' },
        { name: 'Label', type: 'text', componentData: { text: 'Bet: 400', fontSize: 10, fill: '#ffffff', align: 'center' } },
      ];
    case 'config_panel_bg':
    case 'bet_panel_bg':
    case 'auto_count_grid':
    case 'bet_level_grid':
      return [
        { name: 'Background', type: 'sprite' },
      ];
    case 'config_close_btn':
    case 'bet_close_btn':
      return [
        { name: 'Background', type: 'sprite' },
        { name: 'Icon', type: 'sprite' },
      ];
    case 'turbo_toggle':
      return [
        { name: 'Background', type: 'sprite' },
        { name: 'Label', type: 'text', componentData: { text: 'TURBO', fontSize: 12, fill: '#ffffff', align: 'center' } },
      ];
    case 'auto_toggle':
      return [
        { name: 'Background', type: 'sprite' },
        { name: 'Label', type: 'text', componentData: { text: 'AUTO', fontSize: 12, fill: '#ffffff', align: 'center' } },
      ];
    case 'config_confirm_btn':
      return [
        { name: 'Background', type: 'sprite' },
        { name: 'Label', type: 'text', componentData: { text: 'CONFIRM', fontSize: 12, fill: '#ffffff', fontWeight: 'bold', align: 'center' } },
      ];
    case 'bet_display':
      return [
        { name: 'Background', type: 'sprite' },
        { name: 'Text', type: 'text', componentData: { text: '400', fontSize: 16, fill: '#ffffff', align: 'center' } },
      ];
    case 'bet_minus_btn':
      return [
        { name: 'Background', type: 'sprite' },
        { name: 'Label', type: 'text', componentData: { text: '-', fontSize: 14, fill: '#ffffff', align: 'center' } },
      ];
    case 'bet_plus_btn':
      return [
        { name: 'Background', type: 'sprite' },
        { name: 'Label', type: 'text', componentData: { text: '+', fontSize: 14, fill: '#ffffff', align: 'center' } },
      ];
    case 'bet_progress_bar':
      return [
        { name: 'Background', type: 'sprite' },
        { name: 'Fill', type: 'sprite' },
      ];
    case 'bet_confirm_btn':
      return [
        { name: 'Background', type: 'sprite' },
        { name: 'Label', type: 'text', componentData: { text: 'CONFIRM', fontSize: 12, fill: '#ffffff', fontWeight: 'bold', align: 'center' } },
      ];
    default:
      return [];
  }
}

/**
 * Migrates old-format slot game hierarchies to the new format:
 * - Merges turbo_button + auto_button → spin_options
 * - Adds win_banner if missing
 * - Creates sprite/text sub-children for parts that lack them
 */
function migrateSlotHierarchy(gameObjects: Record<string, GameObject>): Record<string, GameObject> {
  const migrated = { ...gameObjects };
  let changed = false;

  // Find all slot game roots
  const slotRoots = Object.values(migrated).filter(
    (go) => go.type === 'backend-slot-game' || go.type === 'cascade-slot'
  );

  for (const root of slotRoots) {
    const rootObj = { ...migrated[root.id] };
    const newChildIds = [...rootObj.childIds];

    // 1. Find and remove turbo_button + auto_button, add spin_options
    const turboIdx = newChildIds.findIndex((id) => {
      const child = migrated[id];
      const pc = child?.components.find((c) => c.type === 'cascade-slot-part');
      return pc?.data?.partType === 'turbo_button';
    });
    const autoIdx = newChildIds.findIndex((id) => {
      const child = migrated[id];
      const pc = child?.components.find((c) => c.type === 'cascade-slot-part');
      return pc?.data?.partType === 'auto_button';
    });

    if (turboIdx !== -1 || autoIdx !== -1) {
      // Remove turbo and auto (delete from gameObjects too)
      const toRemove = [turboIdx, autoIdx].filter((i) => i !== -1).sort((a, b) => b - a);
      for (const idx of toRemove) {
        const removedId = newChildIds.splice(idx, 1)[0];
        delete migrated[removedId];
      }

      // Check if spin_options already exists
      const hasSpinOptions = newChildIds.some((id) => {
        const child = migrated[id];
        const pc = child?.components.find((c) => c.type === 'cascade-slot-part');
        return pc?.data?.partType === 'spin_options';
      });

      if (!hasSpinOptions) {
        const insertIdx = Math.min(turboIdx !== -1 ? turboIdx : newChildIds.length, newChildIds.length);
        const spinOptsObj = createDefaultGameObject('container', 'Spin Options');
        spinOptsObj.parentId = root.id;
        spinOptsObj.transform = { ...spinOptsObj.transform, position: { x: -80, y: 165 } };
        spinOptsObj.components = [{
          id: generateId(),
          type: 'cascade-slot-part',
          enabled: true,
          data: { partType: 'spin_options' },
        }];
        const subDefs = getSubChildDefs('spin_options');
        const subIds = createSubChildren(spinOptsObj.id, subDefs, migrated);
        spinOptsObj.childIds = subIds;
        migrated[spinOptsObj.id] = spinOptsObj;
        newChildIds.splice(insertIdx, 0, spinOptsObj.id);
      }
      changed = true;
    }

    // 2. Add win_banner if missing
    const hasWinBanner = newChildIds.some((id) => {
      const child = migrated[id];
      const pc = child?.components.find((c) => c.type === 'cascade-slot-part');
      return pc?.data?.partType === 'win_banner';
    });
    if (!hasWinBanner) {
      const gridIdx = newChildIds.findIndex((id) => {
        const child = migrated[id];
        const pc = child?.components.find((c) => c.type === 'cascade-slot-part');
        return pc?.data?.partType === 'grid';
      });
      const winBannerObj = createDefaultGameObject('container', 'Win Banner');
      winBannerObj.parentId = root.id;
      winBannerObj.transform = { ...winBannerObj.transform, position: { x: 0, y: 100 } };
      winBannerObj.components = [{
        id: generateId(),
        type: 'cascade-slot-part',
        enabled: true,
        data: { partType: 'win_banner' },
      }];
      const subDefs = getSubChildDefs('win_banner');
      const subIds = createSubChildren(winBannerObj.id, subDefs, migrated);
      winBannerObj.childIds = subIds;
      migrated[winBannerObj.id] = winBannerObj;
      newChildIds.splice(gridIdx !== -1 ? gridIdx + 1 : 2, 0, winBannerObj.id);
      changed = true;
    }

    // 3. Add sub-children to any part container that lacks them
    function addSubChildrenRecursive(parentId: string) {
      const parent = migrated[parentId];
      if (!parent) return;

      for (const childId of parent.childIds) {
        const child = migrated[childId];
        if (!child) continue;

        const partComp = child.components.find((c) => c.type === 'cascade-slot-part');
        if (!partComp) continue;

        const partType = partComp.data.partType as string;
        const subDefs = getSubChildDefs(partType, {
          symbolId: partComp.data.symbolId as string | undefined,
        });

        const hasVisualChildren = child.childIds.some((scId) => {
          const sc = migrated[scId];
          return sc && (sc.type === 'sprite' || sc.type === 'text');
        });

        if (subDefs.length > 0 && !hasVisualChildren) {
          const subIds = createSubChildren(child.id, subDefs, migrated);
          migrated[child.id] = {
            ...migrated[child.id],
            childIds: [...migrated[child.id].childIds, ...subIds],
          };
          changed = true;
        }

        // Recurse into panel children
        addSubChildrenRecursive(childId);
      }
    }

    if (changed) {
      migrated[root.id] = { ...migrated[root.id], childIds: newChildIds };
    }
    addSubChildrenRecursive(root.id);
  }

  return migrated;
}

export const useSceneStore = create<SceneState>()(
  temporal(
  (set, get) => ({
      // Initial state
      sceneId: generateId(),
      sceneName: 'MainScene',
      gameObjects: {},
      rootIds: [],

      // Scene switching state
      currentScenePath: null,
      sceneDirtyFlags: {},
      sceneCache: {},

      selectedIds: [],
      hoveredId: null,
      focusedContainerId: null,
      clipboardIds: [],
      clipboardOperation: null,

      createGameObject: (type, parentId = null) => {
        const typeName = type.charAt(0).toUpperCase() + type.slice(1);
        const name = `New ${typeName}`;
        const gameObject = createDefaultGameObject(type, name);

        // Add default components based on object type
        const defaultComponentTypes = DEFAULT_COMPONENTS_MAP[type] || [];
        gameObject.components = createDefaultComponents(defaultComponentTypes);

        set((state) => {
          const newGameObjects = { ...state.gameObjects };
          newGameObjects[gameObject.id] = gameObject;

          let newRootIds = [...state.rootIds];

          if (parentId && newGameObjects[parentId]) {
            // Add as child
            gameObject.parentId = parentId;
            newGameObjects[parentId] = {
              ...newGameObjects[parentId],
              childIds: [...newGameObjects[parentId].childIds, gameObject.id],
            };
          } else {
            // Add to root
            newRootIds.push(gameObject.id);
          }

          // Auto-create child parts for cascade-slot
          if (type === 'cascade-slot') {
            const cascadeChildren = [
              { name: 'Header',       partType: 'header',        offsetX: 0,   offsetY: -180 },
              { name: 'Slot Machine', partType: 'grid',          offsetX: 0,   offsetY: -40 },
              { name: 'Win Banner',   partType: 'win_banner',    offsetX: 0,   offsetY: 100 },
              { name: 'Balance Text', partType: 'balance_text',  offsetX: -80, offsetY: 130 },
              { name: 'Buy Feature',  partType: 'buy_feature',   offsetX: 0,   offsetY: 130 },
              { name: 'Win Text',     partType: 'win_text',      offsetX: 80,  offsetY: 130 },
              { name: 'Spin Options', partType: 'spin_options',  offsetX: -80, offsetY: 165 },
              { name: 'Spin Button',  partType: 'spin_button',   offsetX: 0,   offsetY: 165 },
              { name: 'Bet Selector', partType: 'bet_selector',  offsetX: 80,  offsetY: 165 },
              { name: 'Game Background', partType: 'game_background', offsetX: 0, offsetY: 0 },
              { name: 'Slot Background', partType: 'slot_background', offsetX: 0, offsetY: 0 },
            ];

            const childIds: string[] = [];

            for (const child of cascadeChildren) {
              const childObj = createDefaultGameObject('container', child.name);
              childObj.parentId = gameObject.id;
              childObj.transform = {
                ...childObj.transform,
                position: { x: child.offsetX, y: child.offsetY },
              };
              childObj.components = [{
                id: generateId(),
                type: 'cascade-slot-part',
                enabled: true,
                data: { partType: child.partType },
              }];
              newGameObjects[childObj.id] = childObj;
              childIds.push(childObj.id);

              // Create sprite/text sub-children for this part
              const subDefs = getSubChildDefs(child.partType);
              if (subDefs.length > 0) {
                const subIds = createSubChildren(childObj.id, subDefs, newGameObjects);
                newGameObjects[childObj.id] = {
                  ...newGameObjects[childObj.id],
                  childIds: [...newGameObjects[childObj.id].childIds, ...subIds],
                };
              }
            }

            newGameObjects[gameObject.id] = {
              ...newGameObjects[gameObject.id],
              childIds: [...newGameObjects[gameObject.id].childIds, ...childIds],
            };
          }

          // Auto-create child parts for backend-slot-game (pixi-slot hierarchy)
          if (type === 'backend-slot-game') {
            const defaultSymbols = [
              { id: 'H1', color: '#DC2626', textColor: '#FFFFFF' },
              { id: 'H2', color: '#7C3AED', textColor: '#FFFFFF' },
              { id: 'H3', color: '#2563EB', textColor: '#FFFFFF' },
              { id: 'H4', color: '#16A34A', textColor: '#FFFFFF' },
              { id: 'N1', color: '#3B82F6', textColor: '#FFFFFF' },
              { id: 'N2', color: '#22C55E', textColor: '#FFFFFF' },
              { id: 'N3', color: '#EF4444', textColor: '#FFFFFF' },
              { id: 'N4', color: '#EAB308', textColor: '#000000' },
              { id: 'N5', color: '#A855F7', textColor: '#FFFFFF' },
              { id: 'SC', color: '#F59E0B', textColor: '#000000' },
              { id: 'BN', color: '#EC4899', textColor: '#FFFFFF' },
            ];
            const defaultRows = 6;
            const defaultCols = 5;

            // Helper to create a child node and wire it into the hierarchy
            function createChild(
              parentId: string,
              childType: GameObjectType,
              childName: string,
              partType?: string,
              extraData?: Record<string, unknown>,
              position?: { x: number; y: number },
              size?: { width: number; height: number },
            ): string {
              const obj = createDefaultGameObject(childType, childName);
              obj.parentId = parentId;
              // Auto-create default components for the type (sprite-renderer, text-renderer, spine-renderer, etc.)
              const defaultComponentTypes = DEFAULT_COMPONENTS_MAP[childType] || [];
              if (defaultComponentTypes.length > 0 && obj.components.length === 0) {
                obj.components = createDefaultComponents(defaultComponentTypes);
              }
              if (partType) {
                obj.components.push({
                  id: generateId(),
                  type: 'cascade-slot-part',
                  enabled: true,
                  data: { partType, ...extraData },
                });
              }
              // Apply position if provided
              if (position) {
                obj.transform.position = { x: position.x, y: position.y };
              }
              // Apply size to component data if provided
              if (size) {
                const spriteComp = obj.components.find(c => c.type === 'sprite-renderer');
                if (spriteComp) {
                  spriteComp.data = { ...spriteComp.data, textureWidth: size.width, textureHeight: size.height };
                }
                // Also store on cascade-slot-part component as customWidth/customHeight
                const partComp = obj.components.find(c => c.type === 'cascade-slot-part');
                if (partComp) {
                  partComp.data = { ...partComp.data, customWidth: size.width, customHeight: size.height };
                }
                // For containers without part component, store directly on transform-like metadata
                if (!spriteComp && !partComp) {
                  // Add a cascade-slot-part component to hold size data
                  obj.components.push({
                    id: generateId(),
                    type: 'cascade-slot-part',
                    enabled: true,
                    data: { partType: 'layout', customWidth: size.width, customHeight: size.height },
                  });
                }
              }
              newGameObjects[obj.id] = obj;
              // Add to parent's childIds
              const parent = newGameObjects[parentId];
              if (parent) {
                parent.childIds = [...parent.childIds, obj.id];
              }
              return obj.id;
            }

            // Helper to create a text node and set its text content
            function createTextChild(parentId: string, childName: string, textContent: string, position?: { x: number; y: number }): string {
              const id = createChild(parentId, 'text', childName, undefined, undefined, position);
              const obj = newGameObjects[id];
              if (obj && obj.components.length > 0) {
                const textComp = obj.components.find(c => c.type === 'text-renderer');
                if (textComp) {
                  textComp.data = { ...textComp.data, text: textContent };
                }
              }
              return id;
            }

            const backendChildIds: string[] = [];

            // Reel x-positions from corrected project config (local to reelContainer)
            const reelXPositions = [-263.45, -133.45, -3.45, 126.55, 256.55];
            // Symbol layout: 6 rows per reel, 140x180 per symbol, 145px step (180 - 35 gap)
            const symbolWidth = 140;
            const symbolHeight = 180;
            const cellStep = 145; // matches live game: symbolHeight + symbolGap (-35)
            // First symbol Y from user's corrected config
            const firstSymbolY = -368.04;

            // ── BackgroundManager ──
            const bgLayerId = createChild(gameObject.id, 'container', 'background-layer', 'game_background', {}, { x: 0, y: 0 }, { width: 720, height: 1280 });
            backendChildIds.push(bgLayerId);
            createChild(bgLayerId, 'spine', 'background', undefined, undefined, { x: 0, y: 0 });

            // ── SlotCore ──
            const slotMachineId = createChild(gameObject.id, 'container', 'slot-machine', 'grid', {}, { x: -5.34, y: -16.45 }, { width: 720, height: 650 });
            backendChildIds.push(slotMachineId);
            const reelContainerId = createChild(slotMachineId, 'container', 'reelContainer', undefined, undefined, { x: 0.79, y: 3.65 }, { width: 660, height: 650 });
            for (let c = 0; c < defaultCols; c++) {
              const reelId = createChild(reelContainerId, 'container', `reel-${c + 1}`, undefined, undefined, { x: reelXPositions[c], y: 0 }, { width: symbolWidth, height: 650 });
              for (let r = 0; r < defaultRows; r++) {
                const symbolY = firstSymbolY + r * cellStep;
                createChild(reelId, 'sprite', `symbol-${r + 1}`, undefined, undefined, { x: 0, y: symbolY }, { width: symbolWidth, height: symbolHeight });
              }
            }
            createChild(slotMachineId, 'container', 'winManager');
            createChild(slotMachineId, 'container', 'frontContainer');

            // ── UIManager ──
            const uiLayerId = createChild(gameObject.id, 'container', 'ui-layer', undefined, undefined, { x: 0, y: 0 });
            backendChildIds.push(uiLayerId);

            // TopGameUI
            const topGameUiId = createChild(uiLayerId, 'container', 'top-game-ui', undefined, undefined, { x: 0, y: -433.92 }, { width: 720, height: 175 });
            createChild(topGameUiId, 'sprite', 'multiplierDisplay', undefined, undefined, { x: 0, y: 5.94 }, { width: 720, height: 175 });

            // ── BottomGameUI ──
            const bottomGameUiId = createChild(uiLayerId, 'container', 'bottom-game-ui', undefined, undefined, { x: 0, y: 594.32 }, { width: 720, height: 479 });
            createChild(bottomGameUiId, 'sprite', 'background', 'slot_background', {}, { x: 0, y: 0 }, { width: 720, height: 479 });

            // SettingButton
            const settingBtnId = createChild(bottomGameUiId, 'container', 'setting-button', undefined, undefined, { x: 332, y: 0 }, { width: 56, height: 59 });
            createChild(settingBtnId, 'sprite', 'Icon', undefined, undefined, { x: 0, y: 0 }, { width: 56, height: 59 });

            // MainPanel
            const mainPanelId = createChild(bottomGameUiId, 'container', 'main-panel', undefined, undefined, { x: 0, y: 0 }, { width: 520, height: 160 });

            // SpinButton
            const spinBtnId = createChild(mainPanelId, 'container', 'spin-button', 'spin_button', {}, { x: 0, y: 0 }, { width: 135, height: 160 });
            createChild(spinBtnId, 'sprite', 'defaultView', undefined, undefined, { x: 0, y: 0 }, { width: 135, height: 160 });
            createChild(spinBtnId, 'sprite', 'pressedView', undefined, undefined, { x: 0, y: 0 }, { width: 136, height: 156 });
            createChild(spinBtnId, 'sprite', 'disabledView', undefined, undefined, { x: 0, y: 0 }, { width: 135, height: 160 });

            // BetOptionButton
            const betOptBtnId = createChild(mainPanelId, 'container', 'bet-option-button', 'bet_selector', {}, { x: 200, y: 0 }, { width: 103, height: 102 });
            createChild(betOptBtnId, 'sprite', 'Icon', undefined, undefined, { x: 0, y: 0 }, { width: 74, height: 78 });
            createTextChild(betOptBtnId, 'Label', 'Bet: 6000', { x: 0, y: 50 });

            // SpinOptionButton
            const spinOptBtnId = createChild(mainPanelId, 'container', 'spin-option-button', 'spin_options', {}, { x: -200, y: 0 }, { width: 137, height: 102 });
            createChild(spinOptBtnId, 'sprite', 'Icon', undefined, undefined, { x: 0, y: 0 }, { width: 74, height: 78 });
            createTextChild(spinOptBtnId, 'Label', 'Spin Options', { x: 0, y: 50 });

            // SecondPanel (hidden by default, same position as MainPanel when toggled)
            const secondPanelId = createChild(bottomGameUiId, 'container', 'second-panel', undefined, undefined, { x: 0, y: 0 }, { width: 484, height: 107 });

            // GameInfoButton
            const gameInfoBtnId = createChild(secondPanelId, 'container', 'game-info-button', undefined, undefined, { x: -200, y: 0 }, { width: 84, height: 107 });
            createChild(gameInfoBtnId, 'sprite', 'Icon', undefined, undefined, { x: 0, y: 0 }, { width: 84, height: 87 });
            createTextChild(gameInfoBtnId, 'Label', 'Info', { x: 0, y: 50 });

            // SoundButton
            const soundBtnId = createChild(secondPanelId, 'container', 'sound-button', undefined, undefined, { x: 0, y: 0 }, { width: 84, height: 107 });
            createChild(soundBtnId, 'sprite', 'Icon', undefined, undefined, { x: 0, y: 0 }, { width: 84, height: 87 });
            createTextChild(soundBtnId, 'Label', 'Sound', { x: 0, y: 50 });

            // HistoryButton
            const historyBtnId = createChild(secondPanelId, 'container', 'history-button', undefined, undefined, { x: 200, y: 0 }, { width: 84, height: 107 });
            createChild(historyBtnId, 'sprite', 'Icon', undefined, undefined, { x: 0, y: 0 }, { width: 84, height: 87 });
            createTextChild(historyBtnId, 'Label', 'History', { x: 0, y: 50 });
            // Set SecondPanel hidden by default
            if (newGameObjects[secondPanelId]) {
              newGameObjects[secondPanelId].visible = false;
            }

            // ThirdPanel
            const thirdPanelId = createChild(bottomGameUiId, 'container', 'third-panel', undefined, undefined, { x: 0, y: -168.78 }, { width: 680, height: 46 });

            // BalanceUI
            const balanceUiId = createChild(thirdPanelId, 'container', 'balance-ui', 'balance_text', {}, { x: -225, y: -25 }, { width: 230, height: 46 });
            createChild(balanceUiId, 'sprite', 'Background', undefined, undefined, { x: 0, y: 0 }, { width: 230, height: 46 });
            createChild(balanceUiId, 'sprite', 'Icon', undefined, undefined, { x: -90, y: 0 }, { width: 30, height: 31 });
            createTextChild(balanceUiId, 'Amount', 'Rp10,000,000', { x: -65, y: 0 });

            // WinUI
            const winUiId = createChild(thirdPanelId, 'container', 'win-ui', 'win_text', {}, { x: 225, y: -25 }, { width: 230, height: 46 });
            createChild(winUiId, 'sprite', 'Background', undefined, undefined, { x: 0, y: 0 }, { width: 230, height: 46 });
            createChild(winUiId, 'sprite', 'Icon', undefined, undefined, { x: -87, y: 0 }, { width: 36, height: 36 });
            createTextChild(winUiId, 'Amount', 'Rp0', { x: -59, y: 0 });

            // BuyFeatureButton
            const buyFeatureBtnId = createChild(bottomGameUiId, 'container', 'buy-feature-button', 'buy_feature', {}, { x: 0, y: -189.69 }, { width: 184, height: 78 });
            createChild(buyFeatureBtnId, 'sprite', 'Icon', undefined, undefined, { x: 0, y: 0 }, { width: 184, height: 78 });
            createTextChild(buyFeatureBtnId, 'Label', 'Buy Feature', { x: 0, y: 0 });

            // FreeSpinContainer (hidden by default)
            const freeSpinContainerId = createChild(bottomGameUiId, 'container', 'free-spin-container', undefined, undefined, { x: 0, y: -250 }, { width: 230, height: 40 });
            createTextChild(freeSpinContainerId, 'counter', 'FREE SPIN: 0');
            // Set FreeSpinContainer visible = false
            if (newGameObjects[freeSpinContainerId]) {
              newGameObjects[freeSpinContainerId].visible = false;
            }

            // ── RewardInformation ──
            const rewardInfoId = createChild(uiLayerId, 'container', 'reward-information', 'win_banner', {}, { x: 0, y: 274.26 }, { width: 720, height: 182 });
            createChild(rewardInfoId, 'sprite', 'background', undefined, undefined, { x: 0, y: 0 }, { width: 720, height: 182 });
            const hintMgrId = createChild(rewardInfoId, 'container', 'HintextManager', undefined, undefined, { x: 0, y: 37.09 }, { width: 440, height: 84 });
            createChild(hintMgrId, 'sprite', 'background', undefined, undefined, { x: 120, y: 10 }, { width: 440, height: 84 });
            createTextChild(hintMgrId, 'hintText', 'WIN UP TO 10 COMBINATIONS');
            // winText (hidden by default)
            const winTextId = createTextChild(rewardInfoId, 'winText', 'WIN', { x: 0, y: -45 });
            if (newGameObjects[winTextId]) {
              newGameObjects[winTextId].visible = false;
            }

            // ── PopupManager (hidden by default) ──
            const popupLayerId = createChild(gameObject.id, 'container', 'popup-layer', undefined, undefined, { x: 0, y: 0 });
            backendChildIds.push(popupLayerId);
            createChild(popupLayerId, 'sprite', 'overlay', undefined, undefined, { x: 0, y: 0 }, { width: 720, height: 1560 });
            createChild(popupLayerId, 'container', 'big-win-popup');
            createChild(popupLayerId, 'container', 'total-win-popup');
            createChild(popupLayerId, 'container', 'total-freespin-popup');
            createChild(popupLayerId, 'container', 'game-information');
            createChild(popupLayerId, 'container', 'history-popup');
            if (newGameObjects[popupLayerId]) {
              newGameObjects[popupLayerId].visible = false;
            }

            newGameObjects[gameObject.id] = {
              ...newGameObjects[gameObject.id],
              childIds: [...newGameObjects[gameObject.id].childIds, ...backendChildIds],
            };
          }

          return {
            gameObjects: newGameObjects,
            rootIds: newRootIds,
            selectedIds: [gameObject.id],
          };
        });

        // Mark scene as dirty
        get().markSceneDirty();

        return gameObject.id;
      },

      deleteGameObject: (id) => {
        set((state) => {
          const gameObject = state.gameObjects[id];
          if (!gameObject) return state;

          // Collect all descendants to delete
          const toDelete = new Set<string>([id]);
          const collectDescendants = (objId: string) => {
            const obj = state.gameObjects[objId];
            if (obj) {
              obj.childIds.forEach((childId) => {
                toDelete.add(childId);
                collectDescendants(childId);
              });
            }
          };
          collectDescendants(id);

          // Create new state
          const newGameObjects = { ...state.gameObjects };
          toDelete.forEach((delId) => delete newGameObjects[delId]);

          // Remove from parent's children
          if (gameObject.parentId && newGameObjects[gameObject.parentId]) {
            newGameObjects[gameObject.parentId] = {
              ...newGameObjects[gameObject.parentId],
              childIds: newGameObjects[gameObject.parentId].childIds.filter(
                (cid) => cid !== id
              ),
            };
          }

          // Remove from root if needed
          const newRootIds = state.rootIds.filter((rid) => rid !== id);

          // Remove from selection
          const newSelectedIds = state.selectedIds.filter(
            (sid) => !toDelete.has(sid)
          );

          // Reset focusedContainerId if it or an ancestor was deleted
          let newFocusedContainerId = state.focusedContainerId;
          if (newFocusedContainerId && toDelete.has(newFocusedContainerId)) {
            newFocusedContainerId = null;
          }

          return {
            gameObjects: newGameObjects,
            rootIds: newRootIds,
            selectedIds: newSelectedIds,
            focusedContainerId: newFocusedContainerId,
          };
        });

        // Mark scene as dirty
        get().markSceneDirty();
      },

      deleteSelected: () => {
        const { selectedIds } = get();
        // Delete in reverse to handle parent-child relationships
        [...selectedIds].reverse().forEach((id) => get().deleteGameObject(id));
      },

      updateGameObject: (id, updates) => {
        set((state) => {
          if (!state.gameObjects[id]) return state;
          return {
            gameObjects: {
              ...state.gameObjects,
              [id]: { ...state.gameObjects[id], ...updates },
            },
          };
        });

        // Mark scene as dirty
        get().markSceneDirty();
      },

      updateTransform: (id, transform) => {
        set((state) => {
          const obj = state.gameObjects[id];
          if (!obj) return state;

          return {
            gameObjects: {
              ...state.gameObjects,
              [id]: {
                ...obj,
                transform: { ...obj.transform, ...transform },
              },
            },
          };
        });

        // Mark scene as dirty
        get().markSceneDirty();
      },

      updateMultipleTransforms: (ids, deltaX, deltaY) => {
        set((state) => {
          const newGameObjects = { ...state.gameObjects };

          // Helper to check if an object or any ancestor is locked
          const checkEffectivelyLocked = (objId: string): boolean => {
            let currentId: string | null = objId;
            while (currentId) {
              const obj = state.gameObjects[currentId];
              if (!obj) return false;
              if (obj.locked) return true;
              currentId = obj.parentId;
            }
            return false;
          };

          ids.forEach((id) => {
            const obj = newGameObjects[id];
            if (obj && !checkEffectivelyLocked(id)) {
              newGameObjects[id] = {
                ...obj,
                transform: {
                  ...obj.transform,
                  position: {
                    x: obj.transform.position.x + deltaX,
                    y: obj.transform.position.y + deltaY,
                  },
                },
              };
            }
          });

          return { gameObjects: newGameObjects };
        });
      },

      addComponent: (id, component) => {
        set((state) => {
          const obj = state.gameObjects[id];
          if (!obj) return state;

          return {
            gameObjects: {
              ...state.gameObjects,
              [id]: {
                ...obj,
                components: [...obj.components, component],
              },
            },
          };
        });

        // Mark scene as dirty
        get().markSceneDirty();
      },

      removeComponent: (objectId, componentId) => {
        set((state) => {
          const obj = state.gameObjects[objectId];
          if (!obj) return state;

          return {
            gameObjects: {
              ...state.gameObjects,
              [objectId]: {
                ...obj,
                components: obj.components.filter((c) => c.id !== componentId),
              },
            },
          };
        });

        // Mark scene as dirty
        get().markSceneDirty();
      },

      updateComponent: (objectId, componentId, data) => {
        set((state) => {
          const obj = state.gameObjects[objectId];
          if (!obj) return state;

          return {
            gameObjects: {
              ...state.gameObjects,
              [objectId]: {
                ...obj,
                components: obj.components.map((c) =>
                  c.id === componentId ? { ...c, data: { ...c.data, ...data } } : c
                ),
              },
            },
          };
        });
      },

      select: (id, addToSelection = false) => {
        set((state) => {
          if (addToSelection) {
            if (state.selectedIds.includes(id)) {
              return { selectedIds: state.selectedIds.filter((s) => s !== id) };
            }
            return { selectedIds: [...state.selectedIds, id] };
          }
          return { selectedIds: [id] };
        });
        if (!addToSelection) {
          get().expandToObject(id);
        }
      },

      selectMultiple: (ids) => set({ selectedIds: ids }),

      selectAll: () => {
        set((state) => ({
          selectedIds: Object.keys(state.gameObjects),
        }));
      },

      clearSelection: () => set({ selectedIds: [], focusedContainerId: null }),

      setHovered: (id) => set({ hoveredId: id }),

      setFocusedContainer: (id) => {
        set({ focusedContainerId: id });
      },

      enterContainer: (id) => {
        set({ focusedContainerId: id });
      },

      exitContainer: () => {
        const { focusedContainerId, gameObjects, rootIds } = get();
        if (!focusedContainerId) return;
        const focused = gameObjects[focusedContainerId];
        if (!focused || !focused.parentId) {
          // At root level, just clear focus
          set({ focusedContainerId: null, selectedIds: focusedContainerId ? [focusedContainerId] : [] });
          return;
        }
        // Move focus up to parent, select the focused container
        const isParentRoot = rootIds.includes(focused.parentId);
        set({
          focusedContainerId: isParentRoot ? null : focused.parentId,
          selectedIds: [focusedContainerId],
        });
      },

      expandToObject: (id) => {
        const { gameObjects } = get();
        const obj = gameObjects[id];
        if (!obj) return;
        const newGameObjects = { ...gameObjects };
        let current = gameObjects[obj.parentId ?? ''];
        let changed = false;
        while (current) {
          if (!current.expanded) {
            newGameObjects[current.id] = { ...current, expanded: true };
            changed = true;
          }
          current = current.parentId ? gameObjects[current.parentId] : undefined!;
        }
        if (changed) {
          set({ gameObjects: newGameObjects });
        }
      },

      reparent: (id, newParentId, insertIndex) => {
        set((state) => {
          const obj = state.gameObjects[id];
          if (!obj) return state;

          // Prevent reparenting to self or descendant
          if (newParentId === id) return state;

          const isDescendant = (parentId: string, childId: string): boolean => {
            const parent = state.gameObjects[parentId];
            if (!parent) return false;
            if (parent.childIds.includes(childId)) return true;
            return parent.childIds.some(cid => isDescendant(cid, childId));
          };

          if (newParentId && isDescendant(id, newParentId)) return state;

          const newGameObjects = { ...state.gameObjects };
          let newRootIds = [...state.rootIds];

          // Remove from old parent/root
          if (obj.parentId) {
            const oldParent = newGameObjects[obj.parentId];
            if (oldParent) {
              newGameObjects[obj.parentId] = {
                ...oldParent,
                childIds: oldParent.childIds.filter((cid) => cid !== id),
              };
            }
          } else {
            newRootIds = newRootIds.filter((rid) => rid !== id);
          }

          // Add to new parent/root
          if (newParentId && newGameObjects[newParentId]) {
            const newParent = newGameObjects[newParentId];
            const newChildIds = [...newParent.childIds];
            if (insertIndex !== undefined) {
              newChildIds.splice(insertIndex, 0, id);
            } else {
              newChildIds.push(id);
            }
            newGameObjects[newParentId] = {
              ...newParent,
              childIds: newChildIds,
            };
            newGameObjects[id] = { ...obj, parentId: newParentId };
          } else {
            if (insertIndex !== undefined) {
              newRootIds.splice(insertIndex, 0, id);
            } else {
              newRootIds.push(id);
            }
            newGameObjects[id] = { ...obj, parentId: null };
          }

          return { gameObjects: newGameObjects, rootIds: newRootIds };
        });

        // Mark scene as dirty
        get().markSceneDirty();
      },

      duplicateGameObject: (id) => {
        const state = get();
        const obj = state.gameObjects[id];
        if (!obj) return '';

        const idMap = new Map<string, string>();

        const duplicateRecursive = (
          sourceId: string,
          newParentId: string | null
        ): string => {
          const source = state.gameObjects[sourceId];
          const newId = generateId();
          idMap.set(sourceId, newId);

          const newObj: GameObject = {
            ...source,
            id: newId,
            name: `${source.name} (Copy)`,
            parentId: newParentId,
            childIds: [],
            components: source.components.map((c) => ({ ...c, id: generateId() })),
          };

          set((s) => ({
            gameObjects: { ...s.gameObjects, [newObj.id]: newObj },
          }));

          // Recursively duplicate children
          source.childIds.forEach((childId) => {
            const newChildId = duplicateRecursive(childId, newObj.id);
            set((s) => ({
              gameObjects: {
                ...s.gameObjects,
                [newObj.id]: {
                  ...s.gameObjects[newObj.id],
                  childIds: [...s.gameObjects[newObj.id].childIds, newChildId],
                },
              },
            }));
          });

          return newId;
        };

        const newId = duplicateRecursive(id, obj.parentId);

        // Add to parent's children or root
        set((s) => {
          if (obj.parentId && s.gameObjects[obj.parentId]) {
            const parent = s.gameObjects[obj.parentId];
            return {
              gameObjects: {
                ...s.gameObjects,
                [obj.parentId]: {
                  ...parent,
                  childIds: [...parent.childIds, newId],
                },
              },
              selectedIds: [newId],
            };
          } else {
            return {
              rootIds: [...s.rootIds, newId],
              selectedIds: [newId],
            };
          }
        });

        // Mark scene as dirty
        get().markSceneDirty();

        return newId;
      },

      copySelected: () => {
        set((state) => ({
          clipboardIds: [...state.selectedIds],
          clipboardOperation: 'copy',
        }));
      },

      cutSelected: () => {
        set((state) => ({
          clipboardIds: [...state.selectedIds],
          clipboardOperation: 'cut',
        }));
      },

      paste: (parentId = null) => {
        const state = get();
        if (state.clipboardIds.length === 0) return;

        const newIds: string[] = [];

        state.clipboardIds.forEach((clipId) => {
          if (state.gameObjects[clipId]) {
            const newId = get().duplicateGameObject(clipId);
            if (newId) {
              newIds.push(newId);
              if (parentId) {
                get().reparent(newId, parentId);
              }
            }
          }
        });

        if (state.clipboardOperation === 'cut') {
          state.clipboardIds.forEach((id) => {
            if (get().gameObjects[id]) {
              get().deleteGameObject(id);
            }
          });
          set({ clipboardIds: [], clipboardOperation: null });
        }

        set({ selectedIds: newIds });
      },

      toggleVisibility: (id) => {
        set((state) => {
          if (!state.gameObjects[id]) return state;
          return {
            gameObjects: {
              ...state.gameObjects,
              [id]: {
                ...state.gameObjects[id],
                visible: !state.gameObjects[id].visible,
              },
            },
          };
        });
      },

      toggleLock: (id) => {
        set((state) => {
          if (!state.gameObjects[id]) return state;
          return {
            gameObjects: {
              ...state.gameObjects,
              [id]: {
                ...state.gameObjects[id],
                locked: !state.gameObjects[id].locked,
              },
            },
          };
        });
      },

      toggleExpanded: (id) => {
        set((state) => {
          if (!state.gameObjects[id]) return state;
          return {
            gameObjects: {
              ...state.gameObjects,
              [id]: {
                ...state.gameObjects[id],
                expanded: !state.gameObjects[id].expanded,
              },
            },
          };
        });
      },

      expandAll: () => {
        set((state) => {
          const updated = { ...state.gameObjects };
          for (const id of Object.keys(updated)) {
            if (updated[id].childIds.length > 0) {
              updated[id] = { ...updated[id], expanded: true };
            }
          }
          return { gameObjects: updated };
        });
      },

      collapseAll: () => {
        set((state) => {
          const updated = { ...state.gameObjects };
          for (const id of Object.keys(updated)) {
            if (updated[id].expanded) {
              updated[id] = { ...updated[id], expanded: false };
            }
          }
          return { gameObjects: updated };
        });
      },

      instantiatePrefab: (prefabData, parentId = null, insertIndex) => {
        const { gameObjects: prefabObjects, rootId } = prefabData;
        if (!rootId || !prefabObjects[rootId]) return null;

        // Generate new IDs for all prefab objects
        const idMap = new Map<string, string>();
        for (const oldId of Object.keys(prefabObjects)) {
          idMap.set(oldId, generateId());
        }

        // Convert each PrefabObjectInput to a full GameObject
        const newGameObjects: Record<string, GameObject> = {};
        for (const [oldId, prefabObj] of Object.entries(prefabObjects)) {
          const newId = idMap.get(oldId)!;
          const objType = (prefabObj.type || 'empty') as GameObjectType;

          // Convert prefab components to scene components
          const components: Component[] = (prefabObj.components || []).map((comp) => ({
            id: generateId(),
            type: (comp.type as string) || 'unknown',
            enabled: true,
            data: (comp.data as Record<string, unknown>) || {},
          }));

          const newObj: GameObject = {
            id: newId,
            name: prefabObj.name,
            type: objType,
            tag: 'Untagged',
            layer: 'Default',
            active: true,
            visible: true,
            locked: false,
            expanded: true,
            parentId: prefabObj.parentId ? (idMap.get(prefabObj.parentId) ?? null) : null,
            childIds: (prefabObj.children || []).map((cid) => idMap.get(cid) || cid),
            transform: {
              position: { ...prefabObj.transform.position },
              rotation: prefabObj.transform.rotation,
              scale: { ...prefabObj.transform.scale },
              anchor: { x: 0.5, y: 0.5 },
            },
            components,
          };

          newGameObjects[newId] = newObj;
        }

        // Set the root object's parent to the target parent
        const newRootId = idMap.get(rootId)!;
        newGameObjects[newRootId].parentId = parentId;

        set((state) => {
          const updatedGameObjects = { ...state.gameObjects, ...newGameObjects };
          let newRootIds = [...state.rootIds];

          if (parentId && updatedGameObjects[parentId]) {
            // Add root of prefab as child of target parent
            const parent = updatedGameObjects[parentId];
            const newChildIds = [...parent.childIds];
            if (insertIndex !== undefined) {
              newChildIds.splice(insertIndex, 0, newRootId);
            } else {
              newChildIds.push(newRootId);
            }
            updatedGameObjects[parentId] = { ...parent, childIds: newChildIds };
          } else {
            // Add to scene root
            if (insertIndex !== undefined) {
              newRootIds.splice(insertIndex, 0, newRootId);
            } else {
              newRootIds.push(newRootId);
            }
          }

          return {
            gameObjects: updatedGameObjects,
            rootIds: newRootIds,
            selectedIds: [newRootId],
          };
        });

        get().markSceneDirty();
        return newRootId;
      },

      getGameObject: (id) => get().gameObjects[id],

      getChildren: (id) => {
        const state = get();
        const obj = state.gameObjects[id];
        return obj ? obj.childIds.map((cid) => state.gameObjects[cid]).filter(Boolean) : [];
      },

      getDescendants: (id) => {
        const state = get();
        const descendants: GameObject[] = [];

        const collect = (objId: string) => {
          const obj = state.gameObjects[objId];
          if (obj) {
            descendants.push(obj);
            obj.childIds.forEach(collect);
          }
        };

        const root = state.gameObjects[id];
        if (root) {
          root.childIds.forEach(collect);
        }

        return descendants;
      },

      findByName: (name) => {
        const state = get();
        const lowerName = name.toLowerCase();
        return Object.values(state.gameObjects).filter((obj) =>
          obj.name.toLowerCase().includes(lowerName)
        );
      },

      isEffectivelyLocked: (id) => {
        const state = get();
        let currentId: string | null = id;

        while (currentId) {
          const obj = state.gameObjects[currentId];
          if (!obj) return false;
          if (obj.locked) return true;
          currentId = obj.parentId;
        }

        return false;
      },

      isEffectivelyVisible: (id) => {
        const state = get();
        let currentId: string | null = id;

        while (currentId) {
          const obj = state.gameObjects[currentId];
          if (!obj) return true; // If object doesn't exist, consider visible
          if (!obj.visible) return false; // If any ancestor is hidden, not visible
          currentId = obj.parentId;
        }

        return true;
      },

      // Scene switching methods
      cacheCurrentScene: (cacheKey?: string) => {
        const state = get();
        const { currentScenePath, sceneId, sceneName, gameObjects, rootIds, selectedIds } = state;

        // Use provided cacheKey, or currentScenePath, or '__untitled__' for unsaved scenes
        const key = cacheKey || currentScenePath || '__untitled__';

        // Get viewport state from sceneViewStore
        const viewportState = useSceneViewStore.getState().viewport;

        const cached: CachedSceneState = {
          sceneId,
          sceneName,
          gameObjects: JSON.parse(JSON.stringify(gameObjects)),
          rootIds: [...rootIds],
          selectedIds: [...selectedIds],
          viewport: {
            panX: viewportState.panX ?? 0,
            panY: viewportState.panY ?? 0,
            zoom: viewportState.zoom ?? 1,
          },
          cachedAt: Date.now(),
        };

        set({
          sceneCache: { ...state.sceneCache, [key]: cached },
        });

        console.log(`[SceneStore] Cached scene: ${key}`);
      },

      loadScene: async (path: string) => {
        const state = get();
        const { currentScenePath, sceneCache } = state;

        // Don't reload the same scene
        if (currentScenePath === path) {
          console.log(`[SceneStore] Scene already loaded: ${path}`);
          return;
        }

        // Step 1: Auto-save current scene to cache (including untitled scenes)
        // Use '__untitled__' key for scenes without a file path
        const cacheKey = currentScenePath || '__untitled__';
        state.cacheCurrentScene(cacheKey);

        // Step 2: Check cache first
        const cached = sceneCache[path];
        if (cached) {
          console.log(`[SceneStore] Loading scene from cache: ${path}`);

          set({
            sceneId: cached.sceneId,
            sceneName: cached.sceneName,
            gameObjects: JSON.parse(JSON.stringify(cached.gameObjects)),
            rootIds: [...cached.rootIds],
            selectedIds: [...cached.selectedIds],
            currentScenePath: path,
          });

          // Restore viewport
          useSceneViewStore.getState().setPan(cached.viewport.panX, cached.viewport.panY);
          useSceneViewStore.getState().setZoom(cached.viewport.zoom);
          return;
        }

        // Step 3: Load from disk
        console.log(`[SceneStore] Loading scene from disk: ${path}`);

        const { readFile, hasProjectRoot } = await import('../services/FileSystemService');
        if (!hasProjectRoot()) {
          throw new Error('No project root - cannot load scene');
        }

        let content: string;
        try {
          content = await readFile(path);
        } catch (error) {
          console.error(`[SceneStore] Failed to read scene file: ${path}`, error);
          throw new Error(`Scene file not found: ${path}`);
        }

        let data: SceneFileFormat;
        try {
          data = JSON.parse(content);
        } catch (error) {
          console.error(`[SceneStore] Invalid scene JSON: ${path}`, error);
          throw new Error(`Invalid scene file format: ${path}`);
        }

        // Convert to store state
        const gameObjects: Record<string, GameObject> = {};
        const rootIds: string[] = [];

        for (const obj of data.objects) {
          gameObjects[obj.id] = {
            id: obj.id,
            name: obj.name,
            type: obj.type,
            tag: obj.tag || 'Untagged',
            layer: obj.layer || 'Default',
            active: obj.active ?? true,
            visible: obj.visible ?? true,
            locked: obj.locked ?? false,
            expanded: obj.expanded ?? true,
            parentId: obj.parentId,
            childIds: obj.childIds || [],
            transform: obj.transform || { ...DEFAULT_TRANSFORM },
            components: obj.components || [],
          };

          if (!obj.parentId) {
            rootIds.push(obj.id);
          }
        }

        // Migrate old-format slot hierarchies
        const migratedGameObjects = migrateSlotHierarchy(gameObjects);

        set({
          sceneId: generateId(),
          sceneName: data.name || 'Untitled Scene',
          gameObjects: migratedGameObjects,
          rootIds,
          selectedIds: [],
          focusedContainerId: null,
          currentScenePath: path,
          sceneDirtyFlags: { ...get().sceneDirtyFlags, [path]: false },
        });

        // Reset viewport for new scene
        useSceneViewStore.getState().resetView();

        console.log(`[SceneStore] Scene loaded: ${path} with ${Object.keys(gameObjects).length} objects`);

        // Clear undo history for new scene
        useSceneStore.temporal.getState().clear();
      },

      saveSceneToDisk: async (path?: string) => {
        const state = get();
        const targetPath = path || state.currentScenePath;

        if (!targetPath) {
          throw new Error('No scene path specified');
        }

        const { writeFile, hasProjectRoot } = await import('../services/FileSystemService');
        if (!hasProjectRoot()) {
          throw new Error('No project root - cannot save scene');
        }

        const sceneData: SceneFileFormat = {
          version: 1,
          name: state.sceneName,
          objects: Object.values(state.gameObjects).map((obj) => ({
            id: obj.id,
            name: obj.name,
            type: obj.type,
            tag: obj.tag,
            layer: obj.layer,
            active: obj.active,
            visible: obj.visible,
            locked: obj.locked,
            expanded: obj.expanded,
            parentId: obj.parentId,
            childIds: obj.childIds,
            transform: obj.transform,
            components: obj.components,
          })),
          settings: {},
        };

        await writeFile(targetPath, JSON.stringify(sceneData, null, 2));

        // Mark scene as clean
        set({
          sceneDirtyFlags: { ...state.sceneDirtyFlags, [targetPath]: false },
        });

        // Update cache
        state.cacheCurrentScene();

        console.log(`[SceneStore] Scene saved to disk: ${targetPath}`);
      },

      isSceneDirty: (path?: string) => {
        const state = get();
        const targetPath = path || state.currentScenePath;
        return targetPath ? state.sceneDirtyFlags[targetPath] ?? false : false;
      },

      markSceneDirty: () => {
        const { currentScenePath } = get();
        if (!currentScenePath) return;

        set((state) => ({
          sceneDirtyFlags: { ...state.sceneDirtyFlags, [currentScenePath]: true },
        }));
      },

      markSceneClean: (path?: string) => {
        const state = get();
        const targetPath = path || state.currentScenePath;
        if (!targetPath) return;

        set({
          sceneDirtyFlags: { ...state.sceneDirtyFlags, [targetPath]: false },
        });
      },

      // Project state methods
      loadProjectState: (state) =>
        set({
          sceneId: state.sceneId ?? '',
          sceneName: state.sceneName ?? 'Untitled Scene',
          currentScenePath: state.currentScenePath ?? null,
          gameObjects: migrateSlotHierarchy(state.gameObjects ?? {}),
          rootIds: state.rootIds ?? [],
          selectedIds: state.selectedIds ?? [],
          // Don't load transient state like hoveredId, clipboard
        }),

      getProjectState: () => {
        const state = get();
        return {
          version: 1,
          lastModified: Date.now(),
          sceneId: state.sceneId || '',
          sceneName: state.sceneName || 'Untitled Scene',
          currentScenePath: state.currentScenePath,
          gameObjects: state.gameObjects || {},
          rootIds: state.rootIds || [],
          selectedIds: state.selectedIds || [],
          sceneViewState: {
            panX: 0,
            panY: 0,
            zoom: 1,
          },
        };
      },

      resetToDefaults: () => {
        set({
          sceneId: generateId(),
          sceneName: 'MainScene',
          currentScenePath: null,
          sceneDirtyFlags: {},
          sceneCache: {},
          gameObjects: {},
          rootIds: [],
          selectedIds: [],
          hoveredId: null,
          focusedContainerId: null,
          clipboardIds: [],
          clipboardOperation: null,
        });
        // Clear undo history on reset
        useSceneStore.temporal.getState().clear();
      },
  }),
  {
    partialize: (state) => ({
      gameObjects: state.gameObjects,
      rootIds: state.rootIds,
      sceneName: state.sceneName,
      sceneId: state.sceneId,
    }),
    limit: 50,
    equality: (pastState, currentState) =>
      pastState.gameObjects === currentState.gameObjects &&
      pastState.rootIds === currentState.rootIds,
  }
  )
);
